'use client';

import Image from 'next/image';
import { ReactNode, useMemo } from 'react';

import {
    Box,
    BoxContent,
    Spacer,
    SpacerSize,
    Text,
    TextLevels,
    Title,
    TitleLevels,
    TitleMarkup,
    TrilogyColor,
    TypographyAlign,
    TypographyBold,
    TypographyColor,
    View,
} from '@bytel/trilogy-react';

import { BigIcon, BigIconVariant } from '@components/big';
import { SmartChangeIcon } from '@components/smart-change';

import LogoBigMobile from '@assets/big/big-b.svg';

export enum CategoryTemplateType {
    SMART_CHANGE = 'smart-change',
    SMALL_PRICE = 'small-price',
    DEFAULT = 'default',
}

type CategoryTemplateProps = {
    hasOnlySmartChangePlans: boolean;
    isSmallPriceCategory: boolean;
    categorySubtitle?: string;
    children: ReactNode;
};

export function CategoryTemplate({
    hasOnlySmartChangePlans,
    isSmallPriceCategory,
    categorySubtitle,
    children,
}: Readonly<CategoryTemplateProps>) {
    const templateType = useMemo((): CategoryTemplateType => {
        if (hasOnlySmartChangePlans) {
            return CategoryTemplateType.SMART_CHANGE;
        }

        if (isSmallPriceCategory) {
            return CategoryTemplateType.SMALL_PRICE;
        }

        return CategoryTemplateType.DEFAULT;
    }, [hasOnlySmartChangePlans, isSmallPriceCategory]);

    const backgroundColor = useMemo((): TrilogyColor => {
        switch (templateType) {
            case CategoryTemplateType.SMART_CHANGE:
                return TrilogyColor.MAIN_FADE;
            case CategoryTemplateType.SMALL_PRICE:
                return TrilogyColor.BACKGROUND;
            case CategoryTemplateType.DEFAULT:
            default:
                return TrilogyColor.NEUTRAL_FADE;
        }
    }, [templateType]);

    const categoryTitle = useMemo((): ReactNode => {
        switch (templateType) {
            case CategoryTemplateType.SMART_CHANGE:
                return (
                    <div className='has-text-centered'>
                        <Title
                            level={TitleLevels.FOUR}
                            markup={TitleMarkup.H3}
                            typo={[TypographyAlign.TEXT_CENTERED, TypographyBold.TEXT_WEIGHT_SEMIBOLD]}
                        >
                            Les forfaits disponibles avec
                        </Title>
                        <SmartChangeIcon height={32} />
                        <Spacer size={SpacerSize.FIVE} />
                    </div>
                );
            case CategoryTemplateType.SMALL_PRICE:
                return (
                    <>
                        <View className='logo-big-container-desktop is-hidden-touch'>
                            <BigIcon variant={BigIconVariant.NEUTRAL} className='toutatis-logo' />
                        </View>
                        <View className='logo-big-container-mobile is-hidden-desktop'>
                            <Image src={LogoBigMobile} alt='' className='toutatis-logo' />
                        </View>
                        <div className='big-z-index'>
                            <Title
                                level={TitleLevels.THREE}
                                markup={TitleMarkup.H3}
                                typo={[
                                    TypographyAlign.TEXT_CENTERED,
                                    TypographyBold.TEXT_WEIGHT_SEMIBOLD,
                                    TypographyColor.TEXT_INFO,
                                ]}
                                className='is-flex is-aligned-center is-justified-center gap-medium'
                            >
                                Les petits <BigIcon variant={BigIconVariant.INFO} height={56} />
                            </Title>
                            <Text
                                level={TextLevels.ONE}
                                typo={[
                                    TypographyAlign.TEXT_CENTERED,
                                    TypographyColor.TEXT_INFO,
                                    TypographyBold.TEXT_WEIGHT_SEMIBOLD,
                                ]}
                            >
                                Des petits prix pour les petits besoins&nbsp;!
                            </Text>
                            <Spacer size={SpacerSize.ONE} />
                        </div>
                    </>
                );
            case CategoryTemplateType.DEFAULT:
            default:
                return (
                    <Title
                        level={TitleLevels.THREE}
                        markup={TitleMarkup.H3}
                        typo={[TypographyAlign.TEXT_CENTERED, TypographyBold.TEXT_WEIGHT_SEMIBOLD]}
                    >
                        {categorySubtitle}
                    </Title>
                );
        }
    }, [templateType, categorySubtitle]);

    return (
        <Box shadowless>
            <BoxContent backgroundColor={backgroundColor} className='is-relative'>
                {categoryTitle}
                {children}
            </BoxContent>
        </Box>
    );
}
